import React, { useState, useEffect } from 'react';
import axios from 'axios';
import constants from '../../constants';
import { cookieStorageUtility } from '../../utils/utilityFunctions';
import { formatDate } from '../RateInventory/Common';
import Loader from '../Common/Loader';
import './RateInventoryDashboard.css';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";

const { serverUrl, ENCRYPTOR_KEY } = constants;
const authUserId = cookieStorageUtility.fetch('auth_user_id');
const encryptedToken = cookieStorageUtility.fetch('encrypted_' + ENCRYPTOR_KEY + '_token');

const RateInventoryDashboard = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [selectedHotel, setSelectedHotel] = useState(null);
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [channelList, setChannelList] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(0); // Default to SYSTEM (0)
  const [errorMessage, setErrorMessage] = useState(null);
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState('all'); // New state for category filter
  const [showRatePopup, setShowRatePopup] = useState(false);
  const [selectedRateData, setSelectedRateData] = useState(null);

  useEffect(() => {
    getMyChannelList();
  }, []); // Fetch channels on component mount

  useEffect(() => {
    if (channelList.length > 0 || selectedUserId === 0) {
      fetchDashboardData();
    }
  }, [startDate, selectedUserId]); // Trigger API call when startDate or selected channel changes

  const getMyChannelList = () => {
    const hotelId = cookieStorageUtility.fetch('hotelId');
    setLoading(true);
    axios(serverUrl + 'channel/getmychannels', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        token: btoa(authUserId + '|' + encryptedToken)
      },
      data: { hotelId }
    }).then(response => {
      if (response.data.responseCode) {
        setChannelList(response.data.data[hotelId] || []);
      } else {
        setChannelList([]);
      }
      setLoading(false);
    }).catch(error => {
      setChannelList([]);
      setLoading(false);
    });
  };

  const fetchDashboardData = async () => {
    setLoading(true);
    setError(null);
    setErrorMessage(null);
    try {
      const hotelId = cookieStorageUtility.fetch('hotelId');
      const requestData = {};
      requestData['data'] = {};
      requestData['data']['userId'] = selectedUserId;
      requestData['data']['categoryId'] = 0;
      requestData['data']['rateplanId'] = 0;
      requestData['data']['startDate'] = startDate;
      requestData['data']['dateRange'] = 10;
      requestData['data']['hotelId'] = hotelId;
      
      const response = await axios(serverUrl + 'rateinventory/inventory', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          token: btoa(authUserId + '|' + encryptedToken)
        },
        data: requestData
      });
      // Handle error response
      if (response.data && response.data.Status === 'error') {
        setErrorMessage(response.data.ErrorMessage || 'An error occurred');
        setDashboardData(null);
        setSelectedHotel(null);
      }
      // Handle success response
      else if (response.data && response.data.Data && response.data.Data.hotels) {
        setDashboardData(response.data.Data);
        
        // Auto-select first hotel if available
        const hotels = response.data.Data.hotels.hotel;
        if (hotels && hotels.length > 0) {
          setSelectedHotel(hotels[0]);
        }
      } else {
        setError('Invalid response format');
      }
    } catch (err) {
      console.error('API Error:', err);
      setError('Error fetching dashboard data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleStartDateChange = (e) => {
    if (!e.target.value){
      setStartDate(new Date().toISOString().split('T')[0]); // Reset to today if empty or in case of invalid date or clear input
    }else{
      setStartDate(e.target.value);
    }

  };

  const handleChannelChange = (e) => {
    setSelectedUserId(parseInt(e.target.value));
  };

  const handleCategoryFilterChange = (e) => {
    setSelectedCategoryFilter(e.target.value);
  };

  const handleNextTenDays = () => {
    const currentStart = new Date(startDate);
    currentStart.setDate(currentStart.getDate() + 10);
    const newDateString = currentStart.toISOString().split('T')[0];
    setStartDate(newDateString);
  };

  const handlePreviousTenDays = () => {
    const currentStart = new Date(startDate);
    currentStart.setDate(currentStart.getDate() - 10);
    const newDateString = currentStart.toISOString().split('T')[0];
    setStartDate(newDateString);
  };

  const handleRateCellClick = (categoryId, date, ratePlan) => {
    // Only show popup for channel mode (not SYSTEM)
    if (selectedUserId === 0) return;
    
    const roomData = getRateForDate(categoryId, date, ratePlan);
    if (!roomData || !roomData.roomType || !roomData.roomType[0]) return;

    // Use existing data instead of making a new API call
    const rateBreakdownData = extractRateBreakdownFromExistingData(categoryId, date, ratePlan, roomData);
    
    setSelectedRateData({
      ...rateBreakdownData,
      categoryName: selectedHotel?.roomCategories?.find(cat => cat.id === categoryId)?.room_category_name || 'Unknown',
      ratePlanName: ratePlan?.channelRatePlanName || 'Unknown',
      date: date.toISOString().split('T')[0],
      currency: roomData.currency || 'IDR' // Include currency from API response
    });
    setShowRatePopup(true);
  };

  const extractRateBreakdownFromExistingData = (categoryId, date, ratePlan, roomData) => {
    // Extract rate breakdown information from the existing roomType data
    const roomType = roomData.roomType[0];
    
    // Based on your actual API response structure, map the correct fields
    return {
      system_price: roomType.system_rate || 0,
      mapping_per_markup: roomType.mapping_markup_rate_per || 0,
      mapping_flat_markup: roomType.mapping_markup_rate_flat || 0,
      special_per_markup: roomType.special_markup_rate_per || 0, // Not available in current API response
      special_flat_markup: roomType.special_markup_rate_flat || 0, // Not available in current API response
      mapping_threshold_rate: roomType.mapping_rate_threshold || 0,
      derivative_price: roomType.derivative_price || 0, // Not available in current API response
      direct_sov: roomType.direct_sov || 0, // Not available in current API response
      derivative: roomType.derivative || 0, // Not available in current API response
      occ_per: roomType.occ_per || 0, // Not available in current API response
      property_max_price: roomType.property_max_price || 0, // Using min_price as closest equivalent
      amount: roomType.aop || 0, // Final calculated amount
      // Additional fields from your API response
      kid_rate: roomType.kid || 0,
      extra_bed_rate: roomType.extraBed || 0,
      tax_amount: roomType.tax || 0,
      min_price: roomType.min_price || 0
    };
  };

  const closeRatePopup = () => {
    setShowRatePopup(false);
    setSelectedRateData(null);
  };

  // Check if previous button should be shown
  const shouldShowPreviousButton = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day
    
    const currentStartDate = new Date(startDate);
    currentStartDate.setHours(0, 0, 0, 0); // Reset time to start of day
    
    const tenDaysFromToday = new Date(today);
    tenDaysFromToday.setDate(today.getDate() + 10);
    
    // Show previous button if current start date is 10 days or more from today
    return currentStartDate >= tenDaysFromToday;
  };

  // Generate exactly 10 days starting from startDate
  const generateDateRange = () => {
    const dates = [];
    const start = new Date(startDate);
    
    for (let i = 0; i < 10; i++) {
      const currentDate = new Date(start);
      currentDate.setDate(start.getDate() + i);
      dates.push(currentDate);
    }
    
    return dates;
  };

  const formatDateHeader = (date) => {
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const day = date.getDate();
    const weekday = date.toLocaleDateString('en-US', { weekday: 'short' });
    
    return { month, day, weekday };
  };

  const formatCurrency = (amount, currency = 'IDR') => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0
    }).format(amount);
  };

  const getRateForDate = (categoryId, date, ratePlanData = null) => {
    if (!selectedHotel || !selectedHotel.rates) return null;
    
    const dateString = date.toISOString().split('T')[0];
    
    for (const rate of selectedHotel.rates) {
      for (const category of rate.category) {
        if (category.roomCategoryId === categoryId.toString()) {
          // If in SYSTEM mode (selectedUserId === 0), ignore ratePlanData and return first matching data
          if (selectedUserId === 0) {
            const dateEntry = category.dates.find(d => d.room.date === dateString);
            if (dateEntry) {
              return {
                ...dateEntry.room,
                currency: rate.currency,
                mealPlanName: rate.mealPlanName,
                channelRatePlanName: category.channel_rate_plan_name,
                mappingId: category.mappingId
              };
            }
          }
          // If ratePlanData is provided (channel mode), match the specific mapping
          else if (ratePlanData && category.mappingId !== ratePlanData.mappingId) {
            continue;
          }
          // For channel mode, find matching date entry
          else {
            const dateEntry = category.dates.find(d => d.room.date === dateString);
            if (dateEntry) {
              return {
                ...dateEntry.room,
                currency: rate.currency,
                mealPlanName: rate.mealPlanName,
                channelRatePlanName: category.channel_rate_plan_name,
                mappingId: category.mappingId
              };
            }
          }
        }
      }
    }
    
    return null;
  };

  // Get all rate plans for a specific room category
  const getRatePlansForCategory = (categoryId) => {
    if (!selectedHotel || !selectedHotel.rates) {
      return [];
    }
    
    const ratePlans = [];
    
    for (const rate of selectedHotel.rates) {
      for (const category of rate.category) {
        if (category.roomCategoryId === categoryId.toString()) {
          ratePlans.push({
            ratePlanId: rate.ratePlanId,
            channelRatePlanName: category.channel_rate_plan_name,
            mappingId: category.mappingId,
            mealPlanName: rate.mealPlanName,
            currency: rate.currency
          });
        }
      }
    }
    
    // If we're in SYSTEM mode (selectedUserId === 0) and we found actual data, 
    // show default BAR rate plan instead of channel-specific rate plans
    if (selectedUserId === 0 && ratePlans.length > 0) {
      return [{
        ratePlanId: null,
        channelRatePlanName: 'BAR',
        mappingId: null,
        mealPlanName: 'European Plan',
        currency: ratePlans[0].currency // Use currency from the actual data
      }];
    }
    
    return ratePlans;
  };

  const getCellClassName = (roomData) => {
    if (!roomData) return 'rate-cell no-data';
    
    let className = 'rate-cell';
    
    if (roomData.stopSell === 'true') {
      className += ' stop-sell';
    } else if (roomData.blackOut === 'true') {
      className += ' blackout';
    } else if (roomData.localAvailCount === 0) {
      className += ' sold-out';
    } else if (roomData.localAvailCount <= 3) {
      className += ' low-availability';
    }
    
    return className;
  };

  // Check if a room category has any data for the selected date range
  const categoryHasData = (category) => {
    if (!selectedHotel || !selectedHotel.rates) {
      return false; // No data available
    }
    
    const dates = generateDateRange();
    
    // Check if any date in the range has data for this category
    for (const date of dates) {
      const roomData = getRateForDate(category.id, date);
      if (roomData) {
        return true; // Found at least one date with data
      }
    }
    
    return false; // No data found for any date in the range
  };

  // Get filtered room categories (only those with data and matching filter)
  const getFilteredRoomCategories = () => {
    const categories = selectedHotel?.roomCategories || [
      { id: 1, room_category_name: 'Standard Room', category_code: 'STD', total_rooms: 'N/A' },
      { id: 2, room_category_name: 'Deluxe Room', category_code: 'DLX', total_rooms: 'N/A' },
      { id: 3, room_category_name: 'Suite', category_code: 'STE', total_rooms: 'N/A' }
    ];
    
    // Filter categories that have data for the selected date range
    let filteredCategories = categories.filter(category => categoryHasData(category));
    
    // Apply category filter if not "all"
    if (selectedCategoryFilter !== 'all') {
      filteredCategories = filteredCategories.filter(category => 
        category.id.toString() === selectedCategoryFilter
      );
    }
    
    return filteredCategories;
  };

  // Get all available room categories for the filter dropdown
  const getAvailableRoomCategories = () => {
    const categories = selectedHotel?.roomCategories || [
      { id: 1, room_category_name: 'Standard Room', category_code: 'STD', total_rooms: 'N/A' },
      { id: 2, room_category_name: 'Deluxe Room', category_code: 'DLX', total_rooms: 'N/A' },
      { id: 3, room_category_name: 'Suite', category_code: 'STE', total_rooms: 'N/A' }
    ];
    
    // Only return categories that have data for the selected date range
    return categories.filter(category => categoryHasData(category));
  };

  const renderCalendarGrid = () => {
    const dates = generateDateRange(); // Always generates exactly 10 days
    
    return (
      <div className="calendar-grid">
        {/* Header Row */}
        <div className="calendar-header">
          <div className="room-header-cell">Room Category / Rate Plan</div>
          {dates.map((date, index) => {
            const dateInfo = formatDateHeader(date);
            return (
              <div key={index} className="date-header-cell">
                <div className="date-month">{dateInfo.month}</div>
                <div className="date-day">{dateInfo.day}</div>
                <div className="date-weekday">{dateInfo.weekday}</div>
              </div>
            );
          })}
        </div>

        {/* Only show room category rows if there's no error message */}
        {!errorMessage && (
          <>
            {/* Room Category Sections */}
            {getFilteredRoomCategories().map((category) => {
              const ratePlans = getRatePlansForCategory(category.id);
              const hasMultipleRatePlans = ratePlans.length > 1;
              
              return (
                <div key={category.id} className="category-section">
                  {/* Category Header Row - Shows inventory count */}
                  <div className="calendar-row category-header-row">
                    <div className="room-category-cell category-header-cell">
                      <div className="room-category-info">
                        <div className="category-name">{category.room_category_name}</div>
                        <div className="category-details">
                          <span className="room-type">({category.category_code})</span>
                          <span className="total-rooms">{category.total_rooms} rooms</span>
                        </div>
                      </div>
                    </div>

                    {/* Inventory cells for each date */}
                    {dates.map((date, dateIndex) => {
                      // Get inventory from any rate plan (they should all have same inventory)
                      const roomData = selectedHotel ? getRateForDate(category.id, date) : null;
                      
                      return (
                        <div key={dateIndex} className="rate-cell inventory-cell">
                          <div className="inventory-content">
                            <div className="inventory-count">
                              {roomData?.localAvailCount || 0}
                            </div>
                            {roomData?.stopSell === 'true' && (
                              <div className="status-indicator stop-sell-indicator">STOP</div>
                            )}
                            {roomData?.blackOut === 'true' && (
                              <div className="status-indicator blackout-indicator">BLACKOUT</div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Rate Plan Rows - Shows prices for each rate plan */}
                  {ratePlans.map((ratePlan, ratePlanIndex) => (
                    <div key={`${category.id}-${ratePlan.mappingId || 'default'}`} className="calendar-row rate-plan-row">
                      <div className="room-category-cell rate-plan-cell">
                        <div className="rate-plan-info">
                          <div className="rate-plan-name">{ratePlan.channelRatePlanName}</div>
                          {hasMultipleRatePlans && (
                            <div className="rate-plan-details">
                              <span className="meal-plan">{ratePlan.mealPlanName}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Price cells for each date */}
                      {dates.map((date, dateIndex) => {
                        const roomData = selectedHotel ? getRateForDate(category.id, date, ratePlan) : null;
                        const cellClassName = getCellClassName(roomData);
                        
                        return (
                          <div key={dateIndex} className={`${cellClassName} price-cell`} onClick={() => handleRateCellClick(category.id, date, ratePlan)}>
                            {roomData ? (
                              <div className="rate-cell-content">
                                <div className="rate-amount">
                                  {formatCurrency(roomData.roomType?.[0]?.aop || 0, roomData.currency)}
                                </div>
                              </div>
                            ) : (
                              <div className="no-rate-content">
                                <div className="rate-amount">NA</div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              );
            })}
          </>
        )}
        
        {/* Error Message Banner - Show when there's an error */}
        {errorMessage && (
          <div className="error-banner">
            <div className="error-banner-content">
              <i className="fa fa-exclamation-triangle"></i>
              <span>{errorMessage}</span>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return <Loader />;
  }

  if (error) {
    return (
      <div className="dashboard-error">
        <div className="error-message">
          <h3>Error Loading Dashboard</h3>
          <p>{error}</p>
          <button onClick={() => fetchDashboardData()} className="retry-btn">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="rate-inventory-dashboard">
      <div className="rate-inventory-section">
        <div className="section-header">
          <div className="section-controls">
            <div className="system-selector">
              <select 
                className="system-dropdown" 
                value={selectedUserId} 
                onChange={handleChannelChange}
              >
                <option value={0}>SYSTEM</option>
                {channelList.map(channel => (
                  <option key={channel.channel_id} value={channel.channel_id}>
                    {channel.ota_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Room Category Filter - Moved next to system selector */}
            <div className="category-filter-control">
              <select 
                className="category-filter-dropdown" 
                value={selectedCategoryFilter} 
                onChange={handleCategoryFilterChange}
              >
                <option value="all">All Categories</option>
                {getAvailableRoomCategories().map(category => (
                  <option key={category.id} value={category.id}>
                    {category.room_category_name}
                  </option>
                ))}
              </select>
            </div>

            {shouldShowPreviousButton() && (
              <div className="previous-days-control">
                <button 
                  className="previous-days-btn"
                  onClick={handlePreviousTenDays}
                >
                  <i className="fa fa-chevron-left"></i>
                  Previous 10 Days
                </button>
              </div>
            )}
            
            <div className="section-date-controls">
              <input
                type="date"
                value={startDate}
                onChange={handleStartDateChange}
              />
            </div>

            <div className="next-days-control">
              <button 
                className="next-days-btn"
                onClick={handleNextTenDays}
              >
                Next 10 Days
                <i className="fa fa-chevron-right"></i>
              </button>
            </div>
          </div>
        </div>

        <div className="calendar-grid-container">
          {renderCalendarGrid()}
        </div>

        {/* Legend */}
        <div className="calendar-legend">
          <div className="legend-item">
            <span className="legend-color available"></span>
            <span>Available</span>
          </div>
          <div className="legend-item">
            <span className="legend-color low-availability"></span>
            <span>Low Availability (≤3)</span>
          </div>
          <div className="legend-item">
            <span className="legend-color sold-out"></span>
            <span>Sold Out</span>
          </div>
          <div className="legend-item">
            <span className="legend-color stop-sell"></span>
            <span>Stop Sell</span>
          </div>
          <div className="legend-item">
            <span className="legend-color blackout"></span>
            <span>Blackout</span>
          </div>
          <div className="legend-item">
            <span className="legend-color no-data"></span>
            <span>No Data (NA)</span>
          </div>
          <div className="legend-item">
            <span className="legend-color rate-not-set"></span>
            <span>Rate Not Set</span>
          </div>
        </div>
      </div>

      {loading && (
        <div className="loading-overlay">
          <Loader />
        </div>
      )}

      {/* Rate Breakdown Popup */}
      {showRatePopup && selectedRateData && (
        <div className="rate-breakdown-popup">
          <div className="popup-content">
            <div className="popup-header">
              <h3>Rate Breakdown</h3>
              <button className="close-popup-btn" onClick={closeRatePopup}>
                <i className="fa fa-times"></i>
              </button>
            </div>

            <div className="popup-body">
              <div className="rate-details">
                <div className="rate-detail">
                  <span className="detail-label">Category:</span>
                  <span className="detail-value">{selectedRateData.categoryName}</span>
                </div>
                <div className="rate-detail">
                  <span className="detail-label">Rate Plan:</span>
                  <span className="detail-value">{selectedRateData.ratePlanName}</span>
                </div>
                <div className="rate-detail">
                  <span className="detail-label">Date:</span>
                  <span className="detail-value">{selectedRateData.date}</span>
                </div>
              </div>

              <div className="breakdown-section">
                <h4>Rate Calculation Details</h4>
                <div className="breakdown-table-container">
                  <table className="breakdown-table">
                    <tbody>
                      <tr>
                        <td className="breakdown-label">System Price</td>
                        <td className="breakdown-value">{formatCurrency(selectedRateData.system_price || 0, selectedRateData.currency)}</td>
                      </tr>
                      <tr>
                        <td className="breakdown-label">Mapping Percentage Markup</td>
                        <td className="breakdown-value">{selectedRateData.mapping_per_markup || 0}%</td>
                      </tr>
                      <tr>
                        <td className="breakdown-label">Mapping Flat Markup</td>
                        <td className="breakdown-value">{formatCurrency(selectedRateData.mapping_flat_markup || 0, selectedRateData.currency)}</td>
                      </tr>
                      <tr>
                        <td className="breakdown-label">Special Percentage Markup</td>
                        <td className="breakdown-value">{selectedRateData.special_per_markup || 0}%</td>
                      </tr>
                      <tr>
                        <td className="breakdown-label">Special Flat Markup</td>
                        <td className="breakdown-value">{formatCurrency(selectedRateData.special_flat_markup || 0, selectedRateData.currency)}</td>
                      </tr>
                      <tr>
                        <td className="breakdown-label">Mapping Threshold Rate</td>
                        <td className="breakdown-value">{formatCurrency(selectedRateData.mapping_threshold_rate || 0, selectedRateData.currency)}</td>
                      </tr>
                      <tr>
                        <td className="breakdown-label">Derivative Price</td>
                        <td className="breakdown-value">{formatCurrency(selectedRateData.derivative_price || 0, selectedRateData.currency)}</td>
                      </tr>
                      <tr>
                        <td className="breakdown-label">Direct SOV</td>
                        <td className="breakdown-value">{(selectedRateData.direct_sov || 0)}%</td>
                      </tr>
                      <tr>
                        <td className="breakdown-label">Derivative</td>
                        <td className="breakdown-value">{(selectedRateData.derivative || 0)}%</td>
                      </tr>
                      <tr>
                        <td className="breakdown-label">Occupancy Percentage</td>
                        <td className="breakdown-value">{selectedRateData.occ_per || 0}%</td>
                      </tr>
                      <tr>
                        <td className="breakdown-label">Property Max Price</td>
                        <td className="breakdown-value">{formatCurrency(selectedRateData.property_max_price || 0, selectedRateData.currency)}</td>
                      </tr>
                      <tr className="final-amount-row">
                        <td className="breakdown-label"><strong>Final Amount</strong></td>
                        <td className="breakdown-value final-amount">{formatCurrency(selectedRateData.amount || 0, selectedRateData.currency)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RateInventoryDashboard;